import { useState, useEffect } from 'react'
import { api } from '../../services/api'
import type { Service, ServiceCategory, PricingType } from '../../services/api'
import PackageManager from './PackageManager'

interface ServiceCatalogProps {
  onAssignToTenant?: (service: Service) => void
}

const CATEGORY_LABELS: Record<ServiceCategory, string> = {
  package: 'Paquete',
  implementation: 'Implementacion',
  consulting: 'Consultoria',
  offensive: 'Seguridad Ofensiva',
  license: 'Licencia',
  automation: 'Automatizacion'
}

const CATEGORY_COLORS: Record<ServiceCategory, string> = {
  package: 'bg-purple-500/20 text-purple-400',
  implementation: 'bg-blue-500/20 text-blue-400',
  consulting: 'bg-green-500/20 text-green-400',
  offensive: 'bg-red-500/20 text-red-400',
  license: 'bg-yellow-500/20 text-yellow-400',
  automation: 'bg-cyan-500/20 text-cyan-400'
}

const PRICING_LABELS: Record<PricingType, string> = {
  per_user_monthly: 'Por usuario/mes',
  one_time: 'Unico',
  hourly: 'Por hora',
  project: 'Por proyecto',
  license_monthly: 'Licencia mensual',
  license_annual: 'Licencia anual'
}

export default function ServiceCatalog({ onAssignToTenant }: ServiceCatalogProps) {
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingService, setEditingService] = useState<Service | null>(null)
  const [filterCategory, setFilterCategory] = useState<ServiceCategory | 'all'>('all')
  const [filterActive, setFilterActive] = useState<boolean | 'all'>('all')
  // Sprint 7: tabs to switch between individual services (legacy view)
  // and packages (SKU bundles — "Shield", "Advance", etc.).
  const [activeTab, setActiveTab] = useState<'services' | 'packages'>('services')

  const fetchServices = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await api.listServices()
      if (!result.success) {
        throw new Error(result.error || 'Error al cargar servicios')
      }
      setServices(result.data?.services || [])
    } catch (err) {
      console.error('Error fetching services:', err)
      setError(err instanceof Error ? err.message : 'Error al cargar servicios')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchServices()
  }, [])

  const handleAddService = () => {
    setEditingService(null)
    setShowForm(true)
  }

  const handleEditService = (service: Service) => {
    setEditingService(service)
    setShowForm(true)
  }

  const handleDeleteService = async (serviceId: string) => {
    if (!confirm('¿Estas seguro de eliminar este servicio?')) return

    try {
      const result = await api.deleteService(serviceId)
      if (!result.success) {
        throw new Error(result.error || 'Error al eliminar servicio')
      }
      await fetchServices()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar')
    }
  }

  const handleSaveService = async (serviceData: Partial<Service>) => {
    try {
      let result
      if (editingService) {
        result = await api.updateService(editingService.id, serviceData)
      } else {
        result = await api.createService(serviceData)
      }

      if (!result.success) {
        throw new Error(result.error || 'Error al guardar servicio')
      }

      await fetchServices()
      setShowForm(false)
      setEditingService(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar')
    }
  }

  const filteredServices = services.filter(service => {
    if (filterCategory !== 'all' && service.category !== filterCategory) return false
    if (filterActive !== 'all' && Boolean(service.is_active) !== filterActive) return false
    return true
  })

  const formatPrice = (price: number, pricingType: PricingType) => {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price)

    const suffix: Record<PricingType, string> = {
      per_user_monthly: '/usuario/mes',
      one_time: '',
      hourly: '/hora',
      project: '',
      license_monthly: '/mes',
      license_annual: '/año'
    }

    return `${formatted}${suffix[pricingType]}`
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jarix-orange mx-auto mb-4"></div>
        <p className="text-gray-400">Cargando catalogo de servicios...</p>
      </div>
    )
  }

  return (
    <div>
      {/* Tab switch: Servicios individuales vs Paquetes vendibles (SKU).
          Sprint 7 added packages as a separate concept — a package is a
          bundle of services you sell under one name/price. */}
      <div className="flex gap-1 mb-6 bg-gray-800 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('services')}
          className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
            activeTab === 'services'
              ? 'bg-gray-700 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Servicios
        </button>
        <button
          onClick={() => setActiveTab('packages')}
          className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
            activeTab === 'packages'
              ? 'bg-gray-700 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Paquetes
          <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-300 align-middle">
            SKU
          </span>
        </button>
      </div>

      {activeTab === 'packages' ? (
        <PackageManager />
      ) : (
      <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">Catalogo de Servicios</h2>
          <p className="text-gray-400 text-sm mt-1">
            {services.length} servicios en total, {services.filter(s => s.is_active).length} activos
          </p>
        </div>
        <button
          onClick={handleAddService}
          className="bg-jarix-orange hover:bg-jarix-pink text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Agregar Servicio
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value as ServiceCategory | 'all')}
          className="bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-jarix-orange focus:outline-none"
        >
          <option value="all">Todas las categorias</option>
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={filterActive === 'all' ? 'all' : filterActive ? 'active' : 'inactive'}
          onChange={(e) => {
            if (e.target.value === 'all') setFilterActive('all')
            else setFilterActive(e.target.value === 'active')
          }}
          className="bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-jarix-orange focus:outline-none"
        >
          <option value="all">Todos los estados</option>
          <option value="active">Activos</option>
          <option value="inactive">Inactivos</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-6">
          {error}
          <button onClick={() => setError(null)} className="float-right">&times;</button>
        </div>
      )}

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredServices.map((service) => (
          <div
            key={service.id}
            className={`bg-gray-800 rounded-xl p-4 border ${
              service.is_active ? 'border-gray-700' : 'border-gray-700/50 opacity-60'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-medium">{service.name}</h3>
                  {!service.is_active && (
                    <span className="text-xs text-gray-500">(Inactivo)</span>
                  )}
                </div>
                <span className={`px-2 py-0.5 rounded text-xs ${CATEGORY_COLORS[service.category]}`}>
                  {CATEGORY_LABELS[service.category]}
                </span>
              </div>
              <div className="flex gap-1">
                {onAssignToTenant && (
                  <button
                    onClick={() => onAssignToTenant(service)}
                    className="text-gray-400 hover:text-green-400 p-1.5 rounded hover:bg-gray-700"
                    title="Asignar a cliente"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => handleEditService(service)}
                  className="text-gray-400 hover:text-jarix-orange p-1.5 rounded hover:bg-gray-700"
                  title="Editar"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDeleteService(service.id)}
                  className="text-gray-400 hover:text-red-400 p-1.5 rounded hover:bg-gray-700"
                  title="Eliminar"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            {service.description && (
              <p className="text-gray-400 text-sm mb-3 line-clamp-2">{service.description}</p>
            )}

            <div className="flex items-center justify-between">
              <div>
                <p className="text-jarix-orange font-semibold">
                  {formatPrice(service.base_price, service.pricing_type)}
                </p>
                <p className="text-gray-500 text-xs">{PRICING_LABELS[service.pricing_type]}</p>
              </div>
              {service.vendor && (
                <span className="text-gray-500 text-xs bg-gray-700 px-2 py-1 rounded">
                  {service.vendor}
                </span>
              )}
            </div>

            {service.cost_price !== undefined && service.cost_price > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-700">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Costo: ${service.cost_price}</span>
                  <span className="text-green-400">
                    Margen: {((service.base_price - service.cost_price) / service.base_price * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredServices.length === 0 && (
        <div className="text-center py-12 bg-gray-800 rounded-xl">
          <svg className="w-12 h-12 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <p className="text-gray-400">No hay servicios que coincidan con los filtros</p>
        </div>
      )}

      {/* Service Form Modal */}
      {showForm && (
        <ServiceFormModal
          service={editingService}
          onSave={handleSaveService}
          onClose={() => {
            setShowForm(false)
            setEditingService(null)
          }}
        />
      )}
      </>
      )}
    </div>
  )
}

// Service Form Modal Component
interface ServiceFormModalProps {
  service: Service | null
  onSave: (data: Partial<Service>) => void
  onClose: () => void
}

function ServiceFormModal({ service, onSave, onClose }: ServiceFormModalProps) {
  const [formData, setFormData] = useState({
    name: service?.name || '',
    description: service?.description || '',
    category: service?.category || 'implementation' as ServiceCategory,
    pricing_type: service?.pricing_type || 'one_time' as PricingType,
    base_price: service?.base_price || 0,
    cost_price: service?.cost_price || 0,
    currency: service?.currency || 'USD',
    vendor: service?.vendor || '',
    vendor_sku: service?.vendor_sku || '',
    is_active: service?.is_active ?? 1,
    sort_order: service?.sort_order || 0
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">
            {service ? 'Editar Servicio' : 'Nuevo Servicio'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Nombre *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-jarix-orange focus:outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Descripcion</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-jarix-orange focus:outline-none"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Categoria *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as ServiceCategory })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-jarix-orange focus:outline-none"
              >
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Tipo de Precio *</label>
              <select
                value={formData.pricing_type}
                onChange={(e) => setFormData({ ...formData, pricing_type: e.target.value as PricingType })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-jarix-orange focus:outline-none"
              >
                {Object.entries(PRICING_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Precio Base (USD) *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.base_price}
                onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) || 0 })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-jarix-orange focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Costo (USD)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.cost_price}
                onChange={(e) => setFormData({ ...formData, cost_price: parseFloat(e.target.value) || 0 })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-jarix-orange focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Proveedor</label>
              <input
                type="text"
                value={formData.vendor}
                onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-jarix-orange focus:outline-none"
                placeholder="ej: CrowdStrike, Cloudflare"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">SKU Proveedor</label>
              <input
                type="text"
                value={formData.vendor_sku}
                onChange={(e) => setFormData({ ...formData, vendor_sku: e.target.value })}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-jarix-orange focus:outline-none"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={Boolean(formData.is_active)}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
              className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-jarix-orange focus:ring-jarix-orange"
            />
            <label htmlFor="is_active" className="text-sm text-gray-300">Servicio activo</label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-jarix-orange text-white rounded-lg hover:bg-jarix-pink transition-colors"
            >
              {service ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
